<template>
  <div>
    <!-- reconnecintg icon -->
    <v-tooltip bottom z-index="999">
      <template v-slot:activator="{ on }">
        <div class="d-flex" v-on="on">
          <v-icon color="white" v-if="displayIcon" class="rotating">mdi-autorenew</v-icon>
        </div>
      </template>
      <span>{{ $t("reconnecting_now") }}</span>
    </v-tooltip>

    <!-- prompt to hard reload when too many reconnects occur -->
    <v-snackbar
      v-model="snackBarPrompt"
      top
      absolute
      multi-line
      transition="fade-transition"
      elevation="10"
      color="grey darken-4"
      :timeout="30000"
    >
      <div class="text-wrap" style="max-width: 320px; text-align: start">
        {{ $t("service_reload_prompt") }}
        <br />
        {{ $t("service_reload_prompt2") }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#e95537" style="font-weight: bolder" v-bind="attrs" @click="forceReloadApp()">
          {{ $t("reload") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { createComponent, value, onMounted, onBeforeDestroy } from "vue-function-api";
import KtznWebsocketService, { IKtznWebsocketService } from "@/framework/network/websocket/ktznWebsocketService";

export default createComponent({
  setup(props, context) {
    const minDisplayInterval = 150000; // i.e. "Don't display tips more frequently than this time"; value in milliseconds
    const maxReconnectBeforePrompt = 10;
    const thisSessionReconnects = value(0); // track # of reconns; over a certain value, we prompt user to reload
    const displayIcon = value(false);
    const snackBarPrompt = value(false);
    const lastTipDisplayTime = value(new Date(1995, 1, 1).getTime()); // we don't want the current date, so instantiate to a dummy date for init check

    const ktznWebsocketService: IKtznWebsocketService = KtznWebsocketService;
    const websocketDisconnected = value(false);

    // setup an interval function for resetting sessionReconnect counter to avoid unecessarilly showing prompt during long session
    //@ts-ignore
    let resetReconnectCounterObject;

    const resetReconnectCounter = () => {
      var resetTime = 600000; // 10 min
      var currentTime = Date.now();
      resetReconnectCounterObject = setInterval(
        function () {
          if (currentTime - lastTipDisplayTime.value > resetTime) return;
          thisSessionReconnects.value = 0;
        },
        resetTime //time in milliseconds
      );
    };

    const forceReloadApp = () => {
      snackBarPrompt.value = false;
      location.reload();
    };

    const onWebsocketConnectHandler = () => {
      // coming from a disconnect signals a recconnect, and should therefore display the notification
      if (websocketDisconnected.value) {
        var currentTime = Date.now();
        thisSessionReconnects.value++;

        if (thisSessionReconnects.value > maxReconnectBeforePrompt) {
          snackBarPrompt.value = true;
          thisSessionReconnects.value = 0;
        } else if (currentTime - lastTipDisplayTime.value > minDisplayInterval && !displayIcon.value) {
          displayIcon.value = true;
          disableIconAfterDelay();
        }

        lastTipDisplayTime.value = currentTime;
        websocketDisconnected.value = false;
      }
    };

    const onWebsocketDisconnectHandler = () => {
      websocketDisconnected.value = true;
    };

    const disableIconAfterDelay = () => {
      setTimeout(() => (displayIcon.value = false), 3000);
    };

    onMounted(() => {
      console.log("SubscriptionReConnect - mounted()");
      resetReconnectCounter();
      disableIconAfterDelay();

      // Register event handlers for conversation repository
      if (ktznWebsocketService) {
        ktznWebsocketService.connected.on(onWebsocketConnectHandler);
        ktznWebsocketService.disconnected.on(onWebsocketDisconnectHandler);
        // doesn't seemed to be used, but including for completeness
        // ktznWebsocketService.failure.on(this.websocketFailureHandler);
      }
    });

    // intervals must be purposefully cleaned up, otherwise they'll run forever, as long as the app is open
    onBeforeDestroy(() => {
      //@ts-ignore
      clearInterval(resetReconnectCounterObject);
    });

    return {
      displayIcon,
      snackBarPrompt,
      forceReloadApp,
      resetReconnectCounter,
    };
  },
});
</script>

<style scoped>
@keyframes rotating {
  from {
    transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
  }
}

.rotating {
  -webkit-animation: rotating 1s linear infinite;
  -moz-animation: rotating 1s linear infinite;
  -ms-animation: rotating 1s linear infinite;
  -o-animation: rotating 1s linear infinite;
  animation: rotating 1s linear infinite;
}

::v-deep .v-snack__wrapper {
  min-width: fit-content;
  min-height: fit-content;
}
</style>
